import {
  isHeadingElement,
  isHtmlElement,
  isListElement,
  isTableElement,
  uniqueIdentifierForNode,
} from '@ctw/shared/content/document-viewer/_internal/renderers/helpers';
import { ReactLinkRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-link-renderer';
import {
  DOCUMENT_VIEWER_BR_TEST_ID,
  DOCUMENT_VIEWER_GENERIC_HTML_SPAN_TEST_ID,
  DOCUMENT_VIEWER_GENERIC_TEXT_SPAN_TEST_ID,
  DOCUMENT_VIEWER_INLINE_TEXT_SPAN_TEST_ID,
} from '@ctw/shared/test/ids';
import { isEmpty } from 'lodash-es';
import { Fragment } from 'react';
import type { CdaSection } from '../../parsers/types';
import { isCdataSection, isInlineTextElement, isTextNode } from '../helpers';
import { ReactHeadingRenderer } from './react-heading-renderer';
import { ReactListRenderer } from './react-list-renderer';
import { ReactTableRenderer } from './react-table-renderer';

interface NodeRendererProps {
  id: string;
  section: CdaSection;
  node: Node;
}

export const ReactNodeRenderer = ({ id, section, node }: NodeRendererProps) => {
  if (isEmpty(node.textContent?.trim())) {
    // Ignore nodes that are effectively empty
    return <></>;
  }

  if (node.nodeName === 'br') {
    return <br data-testid={DOCUMENT_VIEWER_BR_TEST_ID} />;
  }

  if (isTextNode(node) || isCdataSection(node)) {
    const nodeIdentifier = uniqueIdentifierForNode({
      section,
      node,
      context: `node-generictext-${id}`,
    });

    return (
      <span
        id={nodeIdentifier}
        key={nodeIdentifier}
        data-testid={DOCUMENT_VIEWER_GENERIC_TEXT_SPAN_TEST_ID}
      >
        {node.textContent}
      </span>
    );
  }

  if (isHtmlElement(node)) {
    if (['htmlLink', 'a'].includes(node.nodeName)) {
      return <ReactLinkRenderer section={section} link={node} />;
    }

    if (node.nodeName === 'content') {
      const childrenIsSoloTextElement =
        node.childNodes.length === 1 && isInlineTextElement(node.childNodes[0]);

      return (
        <>
          {childrenIsSoloTextElement && node.childNodes[0].textContent}
          {!childrenIsSoloTextElement &&
            Array.from(node.childNodes).map((child, index) => {
              const nodeIdentifier = uniqueIdentifierForNode({
                section,
                node: child,
                context: `node-span-generic-${id}`,
                index,
              });

              return (
                <ReactNodeRenderer
                  id={nodeIdentifier}
                  key={nodeIdentifier}
                  node={child}
                  section={section}
                />
              );
            })}
        </>
      );
    }

    if (isListElement(node)) {
      const listIdentifier = uniqueIdentifierForNode({
        section,
        node,
        context: 'node-nonsemanticlist',
      });

      if (node.querySelector('table,h1,h2,h3,h4,h5,h6')) {
        // The presence of certain elements inside a list element indicate that this is not a HTML-semantic list,
        // but rather a set of items that should be treated as ordered elements. Skip the list item
        // itself and render its children as independent nodes.
        const items = Array.from(node.querySelectorAll('item,li'));

        return (
          <Fragment key={listIdentifier}>
            {items.map((item, index) => {
              const listItemIdentifier = uniqueIdentifierForNode({
                section,
                node: item,
                context: `${listIdentifier}-item`,
                index,
              });

              const childrenIsSoloTextElement =
                item.childNodes.length === 1 && isInlineTextElement(item.childNodes[0]);

              return (
                <Fragment key={listItemIdentifier}>
                  {childrenIsSoloTextElement && item.textContent}
                  {!childrenIsSoloTextElement &&
                    Array.from(item.childNodes).map((child, index) => {
                      const nodeIdentifier = uniqueIdentifierForNode({
                        section,
                        node: item,
                        context: `${listItemIdentifier}-child-${id}`,
                        index,
                      });

                      return (
                        <ReactNodeRenderer
                          id={nodeIdentifier}
                          key={nodeIdentifier}
                          section={section}
                          node={child}
                        />
                      );
                    })}
                </Fragment>
              );
            })}
          </Fragment>
        );
      }

      return (
        <ReactListRenderer id={listIdentifier} key={listIdentifier} section={section} list={node} />
      );
    }

    if (isHeadingElement(node)) {
      return (
        <ReactHeadingRenderer
          key={uniqueIdentifierForNode({ section, node, context: `node-heading-${id}` })}
          section={section}
          heading={node}
        />
      );
    }

    if (isTableElement(node)) {
      const tableIdentifier = uniqueIdentifierForNode({
        section,
        node,
        context: `node-table-${id}`,
      });

      return (
        <ReactTableRenderer
          id={tableIdentifier}
          key={tableIdentifier}
          section={section}
          table={node}
        />
      );
    }

    if (isInlineTextElement(node)) {
      const childrenIsSoloTextElement =
        node.childNodes.length === 1 && isInlineTextElement(node.childNodes[0]);

      const nodeIdentifier = uniqueIdentifierForNode({
        section,
        node,
        context: `node-inlinetext-${id}`,
      });

      return (
        <span
          id={nodeIdentifier}
          key={nodeIdentifier}
          data-testid={DOCUMENT_VIEWER_INLINE_TEXT_SPAN_TEST_ID}
        >
          {childrenIsSoloTextElement && node.childNodes[0].textContent}
          {!childrenIsSoloTextElement &&
            Array.from(node.childNodes).map((child, index) => {
              const childNodeIdentifier = uniqueIdentifierForNode({
                section,
                node,
                context: `${nodeIdentifier}-node-inlinetext-child-${id}`,
                index,
              });

              return (
                <ReactNodeRenderer
                  id={childNodeIdentifier}
                  key={childNodeIdentifier}
                  node={child}
                  section={section}
                />
              );
            })}
        </span>
      );
    }

    const nodeIdentifier = uniqueIdentifierForNode({
      section,
      node,
      context: `node-html-generic-${id}`,
    });

    return (
      <span
        id={nodeIdentifier}
        data-testid={DOCUMENT_VIEWER_GENERIC_HTML_SPAN_TEST_ID}
        key={nodeIdentifier}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <Render elements that we don't know how to handle>
        dangerouslySetInnerHTML={{ __html: node.outerHTML }}
      />
    );
  }

  // Any node that is not Text, CDATA, or an HTML Element should not be rendered.
  return <></>;
};
